import { useState } from "react";
import { createLucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { hashHref } from "../../constants/site";
import {
  CEO_MEDIA,
  EXPERTISE,
  IMPACT,
  LEADERSHIP,
  PARTNER_BENEFITS,
  PARTNER_GROUPS,
  PARTNERS,
  PARTNER_TYPES,
} from "../../constants/about";
import { ArrowIcon, CornerMarks, MediaSlot, useFade, useKnockoutLogo } from "./ui";

const Linkedin = createLucideIcon("linkedin", [
  ["path", { d: "M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" }],
  ["rect", { width: "4", height: "12", x: "2", y: "9" }],
  ["circle", { cx: "4", cy: "4", r: "2" }],
]);

function AboutProof() {
  const { fade, stagger } = useFade();
  const [group, setGroup] = useState("all");
  const visiblePartners =
    group === "all" ? PARTNERS : PARTNERS.filter((partner) => partner.group === group);

  return (
    <>
      <section
        id="impact"
        className="relative scroll-mt-32 overflow-hidden bg-[#f7f6fb] text-navy"
        aria-labelledby="impact-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(55%_40%_at_100%_100%,rgba(79,72,236,0.08),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.36]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Our impact
            </p>
            <h2
              id="impact-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Transforming financial services
              <span className="mt-1 block">across Africa</span>
            </h2>
          </motion.header>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {IMPACT.map((panel) => (
              <motion.article
                key={panel.id}
                className="flex flex-col overflow-hidden border border-navy/10 bg-white"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-navy">
                  <MediaSlot
                    src={panel.image}
                    alt={panel.alt}
                    label={panel.imageLabel}
                    spec={panel.spec}
                    className="absolute inset-0 h-full w-full"
                    position="object-cover object-center"
                  />
                </div>
                <div className="flex flex-1 flex-col px-5 py-6">
                  <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                    {panel.index}
                  </p>
                  <h3 className="mt-3 text-xl font-semibold tracking-tight text-navy">{panel.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/55">{panel.lede}</p>
                  <ul className="mt-4 space-y-2">
                    {panel.items.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-relaxed text-navy/75">
                        <span className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 bg-gold" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="why-partner"
        className="relative scroll-mt-32 overflow-hidden bg-white text-navy"
        aria-labelledby="why-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_45%_at_0%_0%,rgba(255,191,24,0.1),transparent_55%),radial-gradient(50%_40%_at_100%_20%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.3]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto grid max-w-8xl items-start gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16 lg:px-8 lg:py-28">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Why partner with SimFinance
            </p>
            <h2
              id="why-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.5rem)]"
            >
              Measurable value
              <span className="mt-1 block">for every partner</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Partnering with SimFinance means joining an ecosystem built to deliver measurable business
              value. Whether you are a bank, insurance company, fintech, savings and loans institution,
              pension provider or investment company, SimFinance provides the digital tools needed to
              compete and grow in today&apos;s connected financial economy.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Who this is for">
              {PARTNER_TYPES.map((item) => (
                <li
                  key={item}
                  className="border border-navy/10 bg-[#f7f6fb] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70"
                >
                  {item}
                </li>
              ))}
            </ul>
            <a
              href={hashHref("partner")}
              className="mt-8 inline-flex min-h-12 cursor-pointer items-center rounded-full bg-gold px-7 text-base font-semibold text-navy transition hover:bg-[#ffd056] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              Become a partner
            </a>
          </motion.div>

          <motion.ol
            className="grid gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            {PARTNER_BENEFITS.map((item, index) => (
              <motion.li key={item} className="bg-white px-5 py-5" variants={fade}>
                <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="mt-3 text-[15px] font-medium leading-snug text-navy">{item}</p>
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </section>

      <section
        id="leadership"
        className="relative scroll-mt-32 overflow-hidden bg-white text-navy"
        aria-labelledby="leadership-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.1),transparent_55%),radial-gradient(55%_40%_at_100%_8%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.32]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-stretch gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-14">
            <motion.div
              className="relative"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <CornerMarks />
              <div className="relative min-h-[22rem] overflow-hidden border border-navy/10 lg:min-h-full">
                <MediaSlot
                  src={CEO_MEDIA.src}
                  alt={CEO_MEDIA.alt}
                  label={CEO_MEDIA.label}
                  spec={CEO_MEDIA.spec}
                  className="absolute inset-0 h-full w-full"
                  position="object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.article
              className="flex flex-col justify-center"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                Message from our CEO
              </p>
              <h2
                id="leadership-heading"
                className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(1.85rem,4vw,3.1rem)]"
              >
                Leading Africa&apos;s financial future
                <span className="mt-1 block">through innovation</span>
              </h2>
              <p className="mt-2 text-sm font-medium text-navy/45">
                A message from the Chief Executive Officer
              </p>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-navy/65">
                <p>
                  At SimFinance, we believe that technology should do more than automate financial
                  services—it should build trust, expand opportunity and create lasting economic impact.
                </p>
                <p>
                  Across Africa, millions of consumers continue to face challenges accessing reliable
                  financial information, while financial institutions work tirelessly to manage risk,
                  acquire quality customers and improve operational performance.
                </p>
                <p className="font-semibold text-navy">
                  Our vision is to bridge these challenges through one intelligent digital ecosystem.
                </p>
                <p>
                  By combining education, artificial intelligence, digital trust and customer engagement,
                  we are creating a platform that empowers consumers to make informed financial decisions
                  while enabling institutions to serve them more effectively.
                </p>
                <p>
                  Innovation is not simply about building better technology; it is about improving lives,
                  strengthening businesses and accelerating financial inclusion.
                </p>
                <p>
                  As we continue to expand across Ghana and Africa, we remain committed to developing
                  solutions that are secure, intelligent, customer-centric and built for the future.
                </p>
                <p>
                  We invite consumers, financial institutions, regulators and technology partners to join
                  us in building a smarter, more connected financial ecosystem for Africa.
                </p>
                <p className="font-semibold text-navy">
                  Together, we are shaping the future of digital finance.
                </p>
              </div>
              <p className="mt-8 text-sm font-semibold tracking-tight text-navy">
                — Chief Executive Officer, SimFinance
              </p>
            </motion.article>
          </div>

          <motion.div
            className="mt-20"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Our leadership team
            </p>
            <h3 className="mt-4 max-w-3xl font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(1.75rem,3.5vw,2.75rem)]">
              Meet the people driving innovation
            </h3>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60">
              Behind SimFinance is a multidisciplinary team of professionals passionate about transforming
              Africa&apos;s financial services industry through technology. Together, we are building
              intelligent solutions that create value for consumers, financial institutions and the
              broader economy.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Team expertise">
              {EXPERTISE.map((item) => (
                <li
                  key={item}
                  className="border border-navy/10 bg-[#f7f6fb] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy/70"
                >
                  {item}
                </li>
              ))}
            </ul>

            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {LEADERSHIP.map((person, index) => (
                <li key={`${person.role}-${index}`} className="overflow-hidden border border-navy/10 bg-white">
                  <div className="relative aspect-[3/4] bg-navy">
                    <MediaSlot
                      src={person.image}
                      alt={`${person.role} portrait slot`}
                      label={`0${index + 1}  ·  Portrait`}
                      spec={person.spec}
                      className="absolute inset-0 h-full w-full"
                      position="object-cover object-top"
                    />
                  </div>
                  <div className="px-4 py-4">
                    <p className="text-base font-semibold tracking-tight text-navy">{person.name}</p>
                    <p className="mt-1 text-sm text-navy/50">{person.role}</p>
                    {person.linkedin ? (
                      <a
                        href={person.linkedin}
                        className="mt-3 inline-flex min-h-10 cursor-pointer items-center gap-2 text-sm font-semibold text-navy transition hover:text-royal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                      >
                        <Linkedin className="h-4 w-4" aria-hidden="true" />
                        LinkedIn
                      </a>
                    ) : (
                      <p className="mt-3 inline-flex items-center gap-2 text-sm text-navy/35">
                        <Linkedin className="h-4 w-4" aria-hidden="true" />
                        LinkedIn link
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section
        id="partners"
        className="relative scroll-mt-32 overflow-hidden bg-[#f7f6fb] text-navy"
        aria-labelledby="partners-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(55%_40%_at_0%_20%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.36]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Our partners
            </p>
            <h2
              id="partners-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Building success through
              <span className="mt-1 block">strategic collaboration</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              The SimFinance ecosystem is designed to work with a wide range of strategic partners across
              the financial services value chain.
            </p>
          </motion.header>

          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Partner categories">
            {PARTNER_GROUPS.map((item) => {
              const selected = item.id === group;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`partner-tab-${item.id}`}
                  aria-selected={selected}
                  aria-controls="partner-grid"
                  onClick={() => setGroup(item.id)}
                  className={`inline-flex min-h-10 cursor-pointer items-center rounded-full px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                    selected ? "bg-navy text-white" : "border border-navy/10 bg-white text-navy/65 hover:text-navy"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          <div
            id="partner-grid"
            role="tabpanel"
            aria-labelledby={`partner-tab-${group}`}
            className="mt-8"
          >
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {visiblePartners.map((partner) => (
                <PartnerMark key={partner.id} partner={partner} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        id="join"
        className="relative overflow-hidden bg-navy text-white"
        aria-labelledby="join-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.18),transparent_55%),radial-gradient(55%_40%_at_100%_100%,rgba(79,72,236,0.28),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.22]"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto grid max-w-8xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16 lg:px-8 lg:py-28">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-gold/80">
              Join our journey
            </p>
            <h2
              id="join-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              The future of finance
              <span className="mt-1 block">starts here</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg">
              At SimFinance, we are building more than technology—we are building Africa&apos;s next
              generation financial ecosystem. Whether you are a consumer looking for trusted financial
              guidance, a financial institution seeking intelligent growth solutions, or a technology
              partner driving innovation, there is a place for you within the SimFinance ecosystem.
            </p>
            <p className="mt-4 max-w-xl text-base font-semibold leading-relaxed text-white">
              Discover smarter financial solutions. Build stronger partnerships. Shape the future of
              digital finance with SimFinance.
            </p>
          </motion.div>

          <motion.div
            className="relative"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <CornerMarks dark />
            <div className="relative grid gap-px overflow-hidden border border-white/10 bg-white/10">
              {[
                { n: "01", label: "Request a demo", href: hashHref("contact"), hint: "See the platform with a specialist" },
                { n: "02", label: "Become a partner", href: hashHref("partner"), hint: "Join the ecosystem and grow" },
                { n: "03", label: "Talk to the team", href: hashHref("callback"), hint: "Ask a specialist to call you" },
              ].map((action) => (
                <a
                  key={action.label}
                  href={action.href}
                  className="group flex min-h-[4.75rem] cursor-pointer items-center justify-between gap-4 bg-navy px-5 py-4 transition hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold"
                >
                  <span>
                    <span className="block font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                      {action.n}
                    </span>
                    <span className="mt-1 block text-lg font-semibold tracking-tight">{action.label}</span>
                    <span className="mt-0.5 block text-sm text-white/50">{action.hint}</span>
                  </span>
                  <ArrowIcon className="h-4 w-4 shrink-0 text-gold transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function PartnerMark({ partner }) {
  const { href, failed } = useKnockoutLogo(partner.logo);

  return (
    <li className="overflow-hidden border border-navy/10 bg-white">
      <div className="isolate flex aspect-[16/10] items-center justify-center bg-[#f7f6fb] px-6 py-5 sm:px-7">
        {failed || !href ? (
          <p className="text-center text-sm font-semibold text-navy/70">{partner.name}</p>
        ) : (
          <img
            src={href}
            alt={`${partner.name} logo`}
            className="max-h-16 w-auto max-w-[85%] object-contain sm:max-h-[4.5rem]"
          />
        )}
      </div>
      <p className="px-4 py-3 text-sm font-semibold tracking-tight text-navy">{partner.name}</p>
    </li>
  );
}

export default AboutProof;
